'use client'

interface Filter {
  id: string
  label: string
  /** Optional color variant for the filter button when inactive */
  color?: 'default' | 'danger'
}

interface FilterTabsProps {
  /** Array of filter objects with id and label */
  filters: Filter[]
  /** ID of the currently active filter */
  activeFilter: string
  /** Callback when a filter is selected */
  onFilterChange: (filterId: string) => void
  /** Accessible label for the filter group */
  ariaLabel?: string
  /** Remove bottom margin/padding when used inline with other elements */
  inline?: boolean
  /** Color variant: 'light' for light backgrounds, 'dark' for dark backgrounds */
  variant?: 'light' | 'dark'
}

export default function FilterTabs({
  filters,
  activeFilter,
  onFilterChange,
  ariaLabel = 'Filter options',
  inline = false,
  variant = 'light',
}: FilterTabsProps) {
  const isDark = variant === 'dark'

  return (
    <div
      className={`flex gap-2 md:gap-3 overflow-x-auto scrollbar-hide -mx-4 px-4 md:mx-0 md:px-0 md:flex-wrap ${
        inline ? '' : 'pb-2 mb-4 md:mb-6'
      }`}
      role="group"
      aria-label={ariaLabel}
    >
      {filters.map((filter) => {
        const isDanger = filter.color === 'danger'
        const isActive = filter.id === activeFilter

        let buttonClass: string
        if (isActive) {
          buttonClass = isDark
            ? 'bg-white text-brand-navy'
            : 'bg-brand-navy text-white'
        } else if (isDanger) {
          buttonClass = isDark
            ? 'bg-transparent border border-red-400 text-red-400 hover:border-red-300'
            : 'bg-white border border-red-500 text-red-500 hover:border-red-600'
        } else {
          buttonClass = isDark
            ? 'bg-transparent border border-white/40 text-white hover:border-white/70'
            : 'bg-white border border-gray-300 text-gray-700 hover:border-gray-400'
        }

        return (
          <button
            key={filter.id}
            onClick={() => onFilterChange(filter.id)}
            className={`px-4 md:px-5 py-[8px] rounded-full text-[12px] whitespace-nowrap transition-colors outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${
              isDark ? 'focus-visible:ring-white' : 'focus-visible:ring-brand-navy'
            } ${buttonClass}`}
            aria-pressed={isActive}
          >
            {filter.label}
          </button>
        )
      })}
    </div>
  )
}
